import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../utils/dates.js';

export function NotificationBell({ notifications, unreadCount, markRead, markAllRead }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);

  const PANEL_WIDTH = 340;

  const toggle = () => {
    if (!open && buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      // Anchor from the left, clamped so the panel never runs off either
      // edge — the button lives in the narrow sidebar, near the left edge
      // of the viewport, so positioning by distance-from-right (as if it
      // were a typical top-right bell) pushed the whole panel off-screen.
      const left = Math.min(Math.max(r.left, 8), window.innerWidth - PANEL_WIDTH - 8);
      setCoords({ top: r.bottom + 8, left });
    }
    setOpen((o) => !o);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        className="btn-icon"
        onClick={toggle}
        aria-label={`Notifications${unreadCount ? ` — ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        style={{ padding: 'var(--space-2)', position: 'relative' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M15 17H5a1 1 0 01-.707-1.707l1.707-1.707V9a6 6 0 0112 0v4.586l1.707 1.707A1 1 0 0119 17h-4z"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className="notif-badge" style={{ position: 'absolute', top: 0, right: 0 }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && coords && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 150 }}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: 340,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              zIndex: 200,
              overflow: 'hidden',
            }}
            role="region"
            aria-label="Notifications"
          >
            <div className="between" style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ fontWeight: 'var(--weight-semibold)', fontSize: 'var(--text-sm)' }}>Notifications</span>
              {unreadCount > 0 && (
                <button className="btn btn-ghost btn-sm" onClick={markAllRead}>Mark all read</button>
              )}
            </div>
            <ul style={{ maxHeight: 360, overflowY: 'auto' }}>
              {notifications.length === 0 && (
                <li style={{ padding: 'var(--space-6)', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                  No notifications
                </li>
              )}
              {notifications.slice(0, 20).map((n) => (
                <li
                  key={n.id}
                  style={{
                    padding: 'var(--space-3) var(--space-4)',
                    borderBottom: '1px solid var(--color-border)',
                    background: n.read ? 'transparent' : 'var(--color-primary-light)',
                    fontSize: 'var(--text-sm)',
                    display: 'flex',
                    gap: 'var(--space-3)',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p>{n.message}</p>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                      {formatDateTime(n.createdAt)}
                    </p>
                    {n.engagementId && (
                      <Link
                        to={`/engagements/${n.engagementId}`}
                        style={{ fontSize: 'var(--text-xs)' }}
                        onClick={() => { markRead(n.id); setOpen(false); }}
                      >
                        View engagement →
                      </Link>
                    )}
                  </div>
                  {!n.read && (
                    <button
                      className="btn-icon"
                      style={{ padding: 0, minHeight: 'unset', fontSize: 10 }}
                      onClick={() => markRead(n.id)}
                      aria-label="Mark as read"
                    >
                      ✓
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
